'use strict';

/**
 * @fileoverview `@puffin-core/web-address` — the ONE answer to "is this an absolute web address whose
 * scheme this gate allows?" (SECURITY-AND-CONNECTORS-001 §4a).
 *
 * Seven gates asked that question, each by hand: a supplier's website (http or https), a provider's
 * consent URL (https only), a connector registry's endpoints, a partner's webhook target, the subdomain
 * resolver, the ThermoGrid attachment fetch, and the webhook SSRF check. One of them used
 * `URL.canParse`, which the browsers the apps still serve do not have — in those browsers every
 * "Connect" threw before it could leave the page. So:
 *
 * - **Parsed with `new URL(value)`**, in a try, never `URL.canParse`. No base is given, so a relative
 *   reference is not a web address.
 * - **`protocols` is REQUIRED.** There is no default scheme policy: each gate states whether plaintext
 *   http is acceptable, as `WEB_PROTOCOLS.HTTPS_ONLY` or `WEB_PROTOCOLS.HTTP_AND_HTTPS`. A missing,
 *   empty or non-web policy is a programming error and THROWS — it is not a "no".
 * - A value that is not a string is not a web address (`false` / `null`), never an exception: the
 *   gates take untrusted input.
 *
 * @module @puffin-core/web-address
 */

/** The only schemes a web address may carry. */
const HTTP = 'http:';
const HTTPS = 'https:';
const WEB_SCHEMES = Object.freeze([HTTP, HTTPS]);

/** The two scheme policies a gate may state. */
const WEB_PROTOCOLS = Object.freeze({
  HTTPS_ONLY: Object.freeze([HTTPS]),
  HTTP_AND_HTTPS: Object.freeze([HTTP, HTTPS]),
});

/**
 * @param {*} protocols
 * @returns {ReadonlyArray<string>} the validated policy (a `WEB_PROTOCOLS` list is frozen).
 * @throws {TypeError} unless it is a non-empty list of `http:` / `https:`.
 */
function requirePolicy(protocols) {
  if (
    !Array.isArray(protocols) ||
    protocols.length === 0 ||
    !protocols.every((protocol) => WEB_SCHEMES.includes(protocol))
  ) {
    throw new TypeError(
      'web-address: `protocols` is required — WEB_PROTOCOLS.HTTPS_ONLY or WEB_PROTOCOLS.HTTP_AND_HTTPS',
    );
  }
  return protocols;
}

/**
 * @param {*} value
 * @returns {URL|null} `value` as a URL: a URL as given, a string parsed, anything else (or an
 *   unparseable string) null.
 */
function toUrl(value) {
  if (value instanceof URL) return value;
  if (typeof value !== 'string') return null;
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/**
 * The URL when `value` is an absolute URL whose scheme the policy allows; otherwise null.
 *
 * `value` may be a string or an already-parsed `URL`: a gate that must surface its OWN parse failure
 * (the subdomain resolver chains the parser's TypeError as the cause) parses first and hands the URL
 * over, so the scheme decision still lives here.
 *
 * @param {string|URL|*} value
 * @param {{protocols: ReadonlyArray<string>}} options
 * @returns {URL|null}
 * @throws {TypeError} when `protocols` is missing or not a web-scheme policy.
 */
function parseWebAddress(value, { protocols } = {}) {
  const policy = requirePolicy(protocols);
  const url = toUrl(value);
  return url !== null && policy.includes(url.protocol) ? url : null;
}

/**
 * @param {string|URL|*} value
 * @param {{protocols: ReadonlyArray<string>}} options
 * @returns {boolean} whether `value` is an absolute URL whose scheme the policy allows.
 * @throws {TypeError} when `protocols` is missing or not a web-scheme policy.
 */
function isWebAddress(value, options) {
  return parseWebAddress(value, options) !== null;
}

const {
  isOutboundSafeAddress,
  ipAddressFamily,
  isNonPublicHostname,
  outboundHostname,
} = require('./outboundAddress');

module.exports = {
  WEB_PROTOCOLS,
  parseWebAddress,
  isWebAddress,
  isOutboundSafeAddress,
  ipAddressFamily,
  isNonPublicHostname,
  outboundHostname,
};
